import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  Input,
  Checkbox,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Box,
  HStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { FaDownload, FaPaperPlane } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api';

const DownloadModal = ({ isOpen, onClose, projectName, displayName, downloadUrl, downloadFileName, productName, productVersion }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: OTP
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    agreedToUsage: false,
    agreedToTerms: false,
  });

  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false); // For Send OTP button
  const [isVerifying, setIsVerifying] = useState(false); // For Verify & Download button
  const [isResending, setIsResending] = useState(false); // For Resend OTP button
  const [error, setError] = useState('');
  const toast = useToast();

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSendOTP = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!formData.agreedToUsage) {
      setError('Please acknowledge the usage terms');
      return;
    }
    if (!formData.agreedToTerms) {
      setError('Please accept the Terms & Conditions');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/download/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          productName: productName,
          productVersion: productVersion,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep(2);
        toast({
          title: 'OTP Sent! 📧',
          description: 'Check your email for the verification code.',
          status: 'info',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
      } else {
        setError(data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Separate function for resending OTP
  const handleResendOTP = async () => {
    setIsResending(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/download/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          productName: productName,
          productVersion: productVersion,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'OTP Resent! 📧',
          description: 'Check your email for the new verification code.',
          status: 'info',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
      } else {
        setError(data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setIsVerifying(true); // Use separate loading state for verification
    setError('');

    try {
      const response = await fetch(`${API_URL}/download/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          otp: otp,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Verification Successful! 🎉',
          description: 'Your download will begin shortly.',
          status: 'info',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });

        // Add 1 second delay before starting download
        setTimeout(() => {
          // Start download
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = downloadFileName || `${projectName}.zip`;
          // link.download = `${projectName}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Close modal and reset after download starts
          handleClose();
        }, 1000); // 1 second delay

      } else {
        setError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsVerifying(false); // Reset verification loading state
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ name: '', email: '', agreedToUsage: false, agreedToTerms: false });
    setOtp('');
    setError('');
    setIsLoading(false);
    setIsVerifying(false);
    setIsResending(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={false}
      isCentered
    >
      <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.200" />
      <ModalContent
        mx={4}
        borderRadius="20px"
        boxShadow="0 4px 30px rgba(0, 0, 0, 0.1)"
        bg={bgColor}
        backdropFilter="blur(15px)"
        border="1px solid"
        borderColor="whiteAlpha.400"
      >
        <ModalHeader pb={2} textAlign="center">
          <HStack justify="center" spacing={2}>
            <Icon
              as={step === 1 ? FaDownload : FaPaperPlane}
              color="blue.500"
              boxSize={5}
            />
            <Text color={headingColor} fontSize="xl" fontWeight="700">
              {step === 1 ? `Download ${displayName || projectName}` : `Verify Download - ${displayName || projectName}`}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody px={6} pb={6}>
          {error && (
            <Alert status="error" mb={4} borderRadius="8px" fontSize="sm">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 1 ? (
            <VStack spacing={4} as="form">
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Name"
                size="lg"
                borderRadius="8px"
                focusBorderColor="blue.500"
                isRequired
              />

              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Your Email"
                size="lg"
                borderRadius="8px"
                focusBorderColor="blue.500"
                isRequired
              />

              <VStack spacing={3} align="stretch" mt={4}>
                <FormControl>
                  <Checkbox
                    name="agreedToUsage"
                    isChecked={formData.agreedToUsage}
                    onChange={handleInputChange}
                    colorScheme="blue"
                    size="sm"
                    isRequired
                  >
                    <Text fontSize="sm" lineHeight="tall" color={textColor}>
                      I understand this project is a community contribution for learning and personal use, 
                      not for commercial purposes. Basic information like IP/email will be used for quality assurance.
                    </Text>
                  </Checkbox>
                </FormControl>

                <FormControl>
                  <Checkbox
                    name="agreedToTerms"
                    isChecked={formData.agreedToTerms}
                    onChange={handleInputChange}
                    colorScheme="blue"
                    size="sm"
                    isRequired
                  >
                    <Text fontSize="sm" lineHeight="tall" color={textColor}>
                      I accept the{' '}
                      <Text as="span" color="blue.500" textDecoration="underline" cursor="pointer">
                        Terms & Conditions
                      </Text>
                      ,{' '}
                      <Text as="span" color="blue.500" textDecoration="underline" cursor="pointer">
                        Privacy Policy
                      </Text>
                      {' '}and agree to receive project communications.
                    </Text>
                  </Checkbox>
                </FormControl>
              </VStack>

              <Button
                colorScheme="blue"
                size="lg"
                width="100%"
                mt={4}
                onClick={handleSendOTP}
                isLoading={isLoading}
                loadingText="Sending..."
                isDisabled={!formData.name || !formData.email || !formData.agreedToUsage || !formData.agreedToTerms}
                borderRadius="8px"
                bgGradient="linear(135deg, blue.500, blue.600)"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)',
                }}
                transition="all 0.3s ease"
              >
                Send OTP
              </Button>
            </VStack>
          ) : (
            <VStack spacing={6}>
              <Text fontSize="sm" color={textColor} textAlign="center">
                Enter the 6-digit verification code sent to{' '}
                <Text as="span" fontWeight="semibold" color={headingColor}>
                  {formData.email}
                </Text>
              </Text>

              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                size="lg"
                textAlign="center"
                letterSpacing="0.5rem"
                fontSize="1.2rem"
                maxLength={6}
                borderRadius="8px"
                focusBorderColor="blue.500"
              />

              <Button
                colorScheme="blue"
                size="lg"
                width="100%"
                onClick={handleVerifyOTP}
                isLoading={isVerifying} // Use separate loading state
                loadingText="Verifying..."
                isDisabled={otp.length !== 6}
                borderRadius="8px"
                bgGradient="linear(135deg, blue.500, blue.600)"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)',
                }}
                transition="all 0.3s ease"
              >
                Verify & Download
              </Button>

              <Button
                variant="link"
                colorScheme="blue"
                size="sm"
                onClick={handleResendOTP} // Use separate function
                isLoading={isResending} // Use separate loading state
                loadingText="Resending..."
              >
                Resend OTP
              </Button>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DownloadModal;







